import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';

const LIFECYCLE_QKS = [
  ['creators'],
  ['creator'],
  ['leaderboard'],
  ['personal-stats'],
  ['payments-summary'],
];

function invalidateAll(qc: ReturnType<typeof useQueryClient>): void {
  LIFECYCLE_QKS.forEach((qk) => qc.invalidateQueries({ queryKey: qk }));
}

export function useHardDeleteCreator() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => pb.collection('leads').delete(id),
    onSuccess: () => {
      invalidateAll(qc);
      toast('Creator eliminado permanentemente', 'success');
    },
    onError: () => toast('Error al eliminar creator', 'error'),
  });
}

type SoftDeleteVars = { id: string; reason?: string };

export function useSoftDeleteCreator() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (vars: SoftDeleteVars) =>
      pb.send<{ success: boolean }>(`/api/leads/soft-delete/${vars.id}`, {
        method: 'POST',
        body: { reason: vars.reason ?? '' },
      }),
    onSuccess: () => {
      invalidateAll(qc);
      toast('Creator movido a la papelera', 'success');
    },
    onError: (err: Error) => {
      invalidateAll(qc);
      toast(err.message || 'Error al mover a la papelera', 'error');
    },
  });
}

type RestoreRes = { success: boolean; paid_period: boolean };

export function useRestoreCreator() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) =>
      pb.send<RestoreRes>(`/api/leads/restore/${id}`, { method: 'POST' }),
    onSuccess: (r) => {
      invalidateAll(qc);
      toast(
        r.paid_period
          ? 'Restaurado (semana ya pagada, no afecta pagos)'
          : 'Creator restaurado',
        'success'
      );
    },
    onError: (err: Error) =>
      toast(err.message || 'Error al restaurar creator', 'error'),
  });
}
